import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useResponseNotification from 'hooks/useResponseNotification';
import { ListPayload } from 'core/http/response';
import {
    createCommodityCondition,
    deleteCommodityCondition,
    getCommodityCondition,
    listCommodityConditions,
    updateCommodityCondition,
} from 'store/commodity-conditions-center/client';
import {
    CommodityCondition,
    CommodityConditionFilters,
    CommodityConditionRequest,
    CommodityConditionSortDirection,
} from 'store/commodity-conditions-center/commodityCondition';
import getErrorStatus from 'utils/getErrorStatus';
import { deleteCommodityConditionNotifs, saveCommodityConditionNotifs } from 'utils/notificationMessages';

const STALE_VERSION_STATUS = 412;

interface ListConditionsParams extends CommodityConditionFilters {
    pageNumber: number;
    pageSize: number;
    sort?: string;
    sortDirection?: CommodityConditionSortDirection;
}

const useCommodityConditions = () => {
    const [conditions, setConditions] = useState<ListPayload<CommodityCondition>>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<unknown>();
    const { withResponseNotifications } = useResponseNotification();
    const { t } = useTranslation('common');

    const listConditions = useCallback(async ({ pageNumber, pageSize, ...filtersAndSort }: ListConditionsParams) => {
        setIsLoading(true);
        setError(undefined);
        try {
            const payload = await listCommodityConditions({ ...filtersAndSort, page: pageNumber, size: pageSize });
            setConditions(payload);
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // onStale runs when the version is out of date, so the caller can reload the latest one
    const saveWithNotifications = useCallback(
        async (save: Promise<CommodityCondition>, onStale?: () => void) => {
            try {
                const saved = await save;
                return withResponseNotifications(saved, saveCommodityConditionNotifs(t));
            } catch (e) {
                const status = getErrorStatus(e);
                if (status === STALE_VERSION_STATUS) onStale?.();
                return withResponseNotifications(undefined, saveCommodityConditionNotifs(t, status));
            }
        },
        [withResponseNotifications, t]
    );

    const createCondition = useCallback(
        (condition: CommodityConditionRequest) => saveWithNotifications(createCommodityCondition(condition)),
        [saveWithNotifications]
    );

    const editCondition = useCallback(
        (conditionId: string, version: number, condition: CommodityConditionRequest, onStale?: () => void) =>
            saveWithNotifications(updateCommodityCondition(conditionId, version, condition), onStale),
        [saveWithNotifications]
    );

    const deleteCondition = useCallback(
        async ({ id, version }: CommodityCondition) => {
            try {
                await deleteCommodityCondition(id, version);
                return withResponseNotifications(true, deleteCommodityConditionNotifs(t));
            } catch (e) {
                return withResponseNotifications(false, deleteCommodityConditionNotifs(t, getErrorStatus(e)));
            }
        },
        [withResponseNotifications, t]
    );

    return {
        conditions,
        isLoading,
        error,
        listConditions,
        getCondition: getCommodityCondition,
        createCondition,
        editCondition,
        deleteCondition,
    };
};

export default useCommodityConditions;
